import axios from "axios";
import type { CountResponse } from "@/core/network/NodeResponse";
import type { FoxiaBlog, FoxiaBlogCategory } from "@/interfaces/foxia-blog.interface";
import FoxiaBlogRepo from "@/repositories/FoxiaBlogRepo";
import FoxiaBlogCategoriesRepo from "@/repositories/FoxiaBlogCategoriesRepo";
import type { RepoResult } from "@/repositories/repositoryRequest";

// Raw detail payload may be the blog itself or wrapped in `{ data }`.
export type BlogDetailPayload = FoxiaBlog | { data?: FoxiaBlog } | null;

export interface BlogPostLoaderData {
  slug: string;
  detail: BlogDetailPayload;
  related: CountResponse<FoxiaBlog[]> | null;
}

export interface BlogIndexLoaderData {
  blogs: CountResponse<FoxiaBlog[]> | null;
  categories: CountResponse<FoxiaBlogCategory[]> | null;
}

const RELATED_LIMIT = 6;
const INDEX_LIMIT = 12;

// Loaders run on the SSR server (createStaticHandler awaits them) and on the
// client (createBrowserRouter). Fetching here means the server-rendered HTML
// already contains blog content + per-post SEO meta — the whole point of SSR
// for crawlers. Errors are swallowed to null so a flaky API still renders the
// page shell (component shows its error/empty state) instead of a 500.

export async function blogPostLoader({
  params,
}: {
  params: { slug?: string };
}): Promise<BlogPostLoaderData> {
  const slug = params.slug ?? "";

  const [detailResult, relatedResult] = await Promise.all([
    slug
      ? FoxiaBlogRepo.detailBlog(slug)
      : Promise.resolve<RepoResult<FoxiaBlog>>({}),
    FoxiaBlogRepo.getListBlog({ page: 1, limit: RELATED_LIMIT, status: "published" }),
  ]);

  // A genuine "post not found" (API 404) must surface as a real 404 so crawlers
  // don't index the SPA shell at a junk URL. A transient API failure (5xx,
  // network) is NOT a 404 — let the page render its shell/error state so a flaky
  // backend doesn't deindex real posts. We only hard-404 on an explicit 404.
  if (detailResult.error && axios.isAxiosError(detailResult.error)) {
    const status = detailResult.error.response?.status;
    if (status === 404) {
      throw new Response("Not Found", { status: 404 });
    }
  }

  return {
    slug,
    detail: (detailResult.response as BlogDetailPayload) ?? null,
    related: relatedResult.response ?? null,
  };
}

export async function blogIndexLoader(): Promise<BlogIndexLoaderData> {
  const [blogsResult, categoriesResult] = await Promise.all([
    FoxiaBlogRepo.getListBlog({ page: 1, limit: INDEX_LIMIT }),
    FoxiaBlogCategoriesRepo.getListBlogCategories(),
  ]);

  return {
    blogs: blogsResult.response ?? null,
    categories: categoriesResult.response ?? null,
  };
}
